import { action, makeObservable, observable } from 'mobx';

import type { Directories } from '../directories/directories.store';
import { GOPLAN_COLOR_PALETTE_EDITING } from '../roles-store/roles';
import type { RolesStore } from '../roles-store/roles-store';

import { SystemSettingsApi } from './system-settings.api';

export type TSettingsSections = 'palette';

export class SystemSettingsService {
  private readonly api: SystemSettingsApi;
  private readonly rolesStore: RolesStore;
  private settings: Partial<Record<TSettingsSections, object>> = {};

  @observable section: string | null = null;
  @observable subSection: string | null = null;

  constructor(directories: Directories, rolesStore: RolesStore) {
    this.api = new SystemSettingsApi(directories);
    this.rolesStore = rolesStore;

    makeObservable(this);
  }

  @action.bound
  setSection(section: string | null) {
    this.section = section;
  }

  @action.bound
  setSubSection(subSection: string | null) {
    this.subSection = subSection;
  }

  private checkAccess(settingsSection: TSettingsSections): boolean {
    switch (settingsSection) {
      case 'palette': {
        return this.rolesStore.hasAccess([GOPLAN_COLOR_PALETTE_EDITING]);
      }

      default: {
        return true;
      }
    }
  }

  async loadSettings<T extends object = object>(settingsSection: TSettingsSections): Promise<T | null> {
    if (this.settings[settingsSection]) {
      return this.settings[settingsSection] as T;
    }

    try {
      const settings =
        (await (async () => {
          const sett = await this.api.loadSettings<T>(settingsSection);

          if (!sett && this.checkAccess(settingsSection)) {
            const data = await this.api.createSetting<T>(settingsSection, {} as T);
            return data;
          }

          return sett;
        })()) ?? null;

      this.settings[settingsSection] = settings;

      return settings;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async updateSettings<T extends object = object>(
    section: TSettingsSections,
    updatedData: T,
    signal?: AbortSignal,
  ): Promise<T | null> {
    try {
      const settings = (await this.api.updateSettings<T>(section, updatedData, signal)) ?? null;

      this.settings[section] = settings;

      return settings;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  init() {}
}
